import { Loading, Panel, Tooltip } from "@appkit4/react-components";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import utc from "dayjs/plugin/utc";
import { useContext, useEffect, useMemo, useState } from "react";

import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AutoSizer, List } from "react-virtualized";
import CompanyPanel from "../../components/CompanyPanel";
import CustomTooltipIcon from "../../components/customTooltipIcon/customTooltipIcon";
import toast from "../../components/DismissibleToast";
import { BackButton, NextButton } from "../../components/ProgressNav";
import { Button, Checkbox } from "../../components/ReadonlyAwareInputs";
import RoundedBorder from "../../components/ui/RoundedBorder";
import { AuthContext } from "../../services/AuthProvider";
import { get_company_periods_only } from "../../services/dredger/dredger.service";
import {
  canProgressCalculation,
  disableProgressInViewMode,
  getCurrentPeriodIsReadonly,
  getGroupData,
  getPageTooltips,
  getPoaIndex,
  getSelectedPeriod,
  setGroupData,
} from "../../services/GroupContext";
import sqldatabaseService from "../../services/sqldatabase/sqldatabase.service";
import {
  calculateCapProps,
  getCompanyAPsforPoA,
  initAdjustedCaps,
} from "../../utils/adjustedCapsUtil";
import { processDateJS } from "../../utils/dateProcessor";
import {
  get_relevant_companies,
  get_relevant_periods,
} from "../../utils/PeriodUtils";
import APModal from "./APModal";
dayjs.extend(utc);
dayjs.extend(isBetween);

const CompanyAP = () => {
  const [saved_data, set_saved_data] = useState();
  const [companies, setCompanies] = useState([]);

  const [isPosting, setIsPosting] = useState(false);
  const navigate = useNavigate();
  const selectedPeriod = useMemo(() => getSelectedPeriod(), []);
  const poaIndex = useMemo(() => getPoaIndex(), []);
  const disableViewModeProgress = useMemo(
    () => disableProgressInViewMode(),
    [],
  );
  const tooltips = useMemo(() => getPageTooltips("companyAPs"), []);
  const currentPoACompanyAPs = useMemo(
    () => getCompanyAPsforPoA(selectedPeriod, saved_data),
    [saved_data],
  );
  const { period_start, period_end } = selectedPeriod;

  const auth = useContext(AuthContext);

  const [isError, setIsError] = useState(false);
  const [conflictingAPs, setConflictingAPs] = useState([]);

  const [selectedCompany, setSelectedCompany] = useState();

  const [show_ap_modal, set_show_ap_modal] = useState(false);

  const [loading, set_loading] = useState(true);
  const [is_refreshing, set_is_refreshing] = useState(false);
  const [isCreatingAutomaticAPs, setIsCreatingAutomaticAPs] = useState(false);
  const isReadOnly = auth.isReadOnlyUser();
  const isClientFacing = useSelector((state) => state.usageInfo.isClientFacing);

  const rowHeight = 55;
  const [listHeight, setListHeight] = useState(rowHeight);

  const gridTemplate1 = "56px 1.96fr 1fr 1fr 1fr 1fr 2fr 1fr 140px";
  const gridTemplate2 = "56px 2fr 1fr 1fr 1fr 1fr 2fr 1fr 140px";

  const clientFacingGridTemplate1 = "1.96fr 1fr 1fr 1fr 1fr 2fr 1fr 140px";
  const clientFacingGridTemplate2 = "2fr 1fr 1fr 1fr 1fr 2fr 1fr 140px";

  useEffect(() => {
    setListHeight(
      Math.min(companies.length * rowHeight, window.innerHeight * 0.78),
    );
  }, [companies]);

  const onAPModalSave = (grpData) => {
    set_saved_data(grpData);
  };

  useEffect(() => {
    const fetchData = async () => {
      let group_data = getGroupData();
      if (!group_data) {
        return;
      }

      if (!group_data?.periods_of_account) {
        return;
      }

      if (!period_start || !period_end) {
        return;
      }

      set_saved_data(group_data);

      const relevant_companies = get_relevant_companies(
        period_start,
        period_end,
        group_data,
      );
      setCompanies(relevant_companies);

      const relevant_caps = get_relevant_periods(
        period_start,
        period_end,
        group_data,
      );
      const userEmail = auth?.getEmail();

      const poa = group_data?.periods_of_account[poaIndex];
      const onesource_companies = relevant_companies?.filter(
        (c) => c?.from_onesource,
      );
      if (
        relevant_caps?.length === 0 &&
        !poa?.onesource_cap_extration &&
        onesource_companies?.length > 0
      ) {
        // No caps - import from OneSource

        try {
          const company_accounting_periods = await get_company_periods_only(
            onesource_companies,
            period_start,
            period_end,
            userEmail,
          );

          poa.onesource_cap_extration = true;
          group_data.periods_of_account[poaIndex] = poa;
          const current_periods = group_data?.company_accounting_periods || [];
          group_data = {
            ...group_data,
            company_accounting_periods: [
              ...current_periods,
              ...company_accounting_periods,
            ],
          };

          set_saved_data(group_data);
        } catch (error) {
          console.error(
            "Failed to fetch OneSource data on initial page visit",
            error,
          );
          toast.error(
            "Something went wrong while fetching data from OneSource",
          );
        }
      }

      set_loading(false);
    };

    fetchData();
  }, []);

  // when modal is closed recalculate coverage
  useEffect(() => {
    if (show_ap_modal) return;

    if (companies?.length > 0) {
      let hasFullCoverage = true;
      let updatedCompanies = [...companies];
      for (const company of updatedCompanies) {
        const id = company?.company_ID;
        const coverage = calculate_poa_coverage(id);
        company.poa_coverage = coverage;

        if (coverage < 100) {
          hasFullCoverage = false;
        }
      }
      setCompanies(updatedCompanies);
      setIsError(!hasFullCoverage);
    }
  }, [show_ap_modal, saved_data]);

  useEffect(() => {
    // Check if any PoAs have a refreshing key. If so, there is a conflict which hasn't been resolved
    const caps = saved_data?.company_accounting_periods;
    if (!caps) {
      return;
    }

    setConflictingAPs(caps?.filter((cap) => cap?.refreshing?.colour === "red"));
  }, [saved_data]);

  const removeDuplicates = (caps) => {
    if (!caps) {
      return [];
    }
    const seen = new Set();
    return caps?.filter((cap) => {
      const datePair = `${cap?.company_ID}-${cap?.start_date}-${cap?.end_date}`;
      if (seen?.has(datePair)) {
        return false;
      } else {
        seen?.add(datePair);
        return true;
      }
    });
  };

  const calculate_poa_coverage = (company_ID) => {
    let caps = getCompanyAPsWithinPoA(company_ID);
    if (!caps) {
      return 0;
    }

    const company = companies?.find((comp) => comp?.company_ID === company_ID);
    if (!company) {
      return 0;
    }

    if (caps?.length < 1) {
      return 0;
    }

    caps = removeDuplicates(caps);
    // period to be considered starts at the latest of the period start date and the date the company joined the group
    const period_start_date = dayjs.max(
      processDateJS(period_start),
      processDateJS(company?.date_join),
    );
    // and ends at the minimum of the period start date and the date the company left the group
    const period_end_date = dayjs.min(
      processDateJS(period_end),
      processDateJS(company?.date_left)?.subtract(1, "day"),
    );
    let coveredDays = 0;
    caps?.forEach((cap) => {
      const rangeStartDate = processDateJS(cap?.start_date);
      const rangeEndDate = processDateJS(cap?.end_date);

      // Find the overlapping period between the date range and the given period
      const overlapStart = dayjs.max(rangeStartDate, period_start_date);
      const overlapEnd = dayjs.min(rangeEndDate, period_end_date);

      // Calculate the number of overlapping days (inclusive)
      if (
        overlapStart?.isBefore(overlapEnd) ||
        overlapStart?.isSame(overlapEnd)
      ) {
        const overlapDays = overlapEnd?.diff(overlapStart, "day") + 1;
        coveredDays += overlapDays;
      }
    });
    const periodLength =
      processDateJS(period_end_date)?.diff(
        processDateJS(period_start_date),
        "days",
      ) + 1;

    const coverage = Math.min(100, (coveredDays / periodLength) * 100);
    return parseFloat(coverage?.toFixed(2));
  };

  const getCompanyAPsWithinPoA = (companyID) => {
    if (!companyID) {
      return [];
    }

    let filteredAPs =
      removeDuplicates(
        currentPoACompanyAPs?.filter((ap) => ap.company_ID === companyID),
      ) ?? [];

    return filteredAPs;
  };

  const createMissingAPs = () => {
    setIsCreatingAutomaticAPs(true);
    const newAP = (company_ID, startDate, endDate) => {
      return {
        company_ID,
        start_date: processDateJS(startDate),
        end_date: processDateJS(endDate),
        end_outside_poa: false,
        start_outside_poa: false,
      };
    };
    const createAPsForCompany = (company_ID, caps = [], startDate, endDate) => {
      const tempEndDate = processDateJS(startDate)
        ?.add(1, "year")
        .subtract(1, "day");
      if (tempEndDate?.isSameOrAfter(processDateJS(endDate))) {
        caps.push(newAP(company_ID, startDate, endDate));
        return caps;
      } else {
        caps.push(newAP(company_ID, startDate, tempEndDate));
        return createAPsForCompany(
          company_ID,
          caps,
          processDateJS(tempEndDate)?.add(1, "day"),
          endDate,
        );
      }
    };
    const companiesWithoutAPs = companies
      .filter((comp) => comp.poa_coverage === 0)
      .map((comp) => comp.company_ID);
    let newAccountingPeriods = [];
    companiesWithoutAPs.forEach((company_ID) => {
      newAccountingPeriods = [
        ...newAccountingPeriods,
        ...createAPsForCompany(company_ID, [], period_start, period_end),
      ];
    });
    set_saved_data((prevState) => ({
      ...prevState,
      company_accounting_periods: [
        ...prevState.company_accounting_periods,
        ...newAccountingPeriods,
      ],
    }));
    setIsCreatingAutomaticAPs(false);
  };

  const save = () => {
    // Ensure all companies have 100% PoA coverage
    for (const company of companies) {
      const id = company?.company_ID;
      const coverage = company.poa_coverage;
      if (coverage < 100) {
        throw `${company.company_name}'s PoA coverage must be 100% before proceeding`;
      }
    }

    const any_conflict = saved_data?.company_accounting_periods?.some(
      (cap) => cap?.refreshing?.colour === "red",
    );
    if (any_conflict) {
      throw "There are still unresolved conflicts. Please resolve them!";
    }

    setIsPosting(true);

    if (isReadOnly || getCurrentPeriodIsReadonly()) {
      return;
    }

    const selectedPeriod = getSelectedPeriod();

    const companyLookupMap = new Map(
      saved_data?.companies?.map((c) => [c.company_ID, c]) ?? [],
    );

    saved_data.company_accounting_periods =
      saved_data?.company_accounting_periods.map((cap) =>
        calculateCapProps(saved_data, selectedPeriod, cap, companyLookupMap),
      );

    const adjusted_caps = initAdjustedCaps(saved_data);

    const canProgressCalc = canProgressCalculation();

    const currentPoA = saved_data.periods_of_account[poaIndex];
    saved_data.periods_of_account[poaIndex] = {
      ...currentPoA,
      adjusted_caps,
      ...canProgressCalc,
    };
    setGroupData(saved_data);

    const response = sqldatabaseService?.postResponse(saved_data);

    const onSuccess = () => {
      setGroupData(saved_data);
      setIsPosting(false);
    };
    return toast.save(response, onSuccess, () => setIsPosting(false));
  };

  const [all_selected, set_all_selected] = useState(false);
  const [selected_companies, set_selected_companies] = useState([]);

  const handle_select_all = (value, event) => {
    if (
      event?.target?.parentNode?.className?.indexOf("ap-checkbox-label") > -1
    ) {
      return;
    }

    set_all_selected((value) => !value);
    if (value) {
      const onesource_companies = companies?.filter((c) => c?.from_onesource);
      set_selected_companies([...onesource_companies]);
    } else {
      set_selected_companies([]);
    }
  };

  useEffect(() => {
    const onesource_companies = companies?.filter((c) => c?.from_onesource);
    if (selected_companies?.length === onesource_companies?.length) {
      set_all_selected(true);
    } else {
      set_all_selected(false);
    }
  }, [selected_companies, companies]);

  const refresh_from_onesource = async () => {
    if (selected_companies?.length === 0) {
      toast.error("Select companies from the table to refresh from OneSource");
      return;
    }

    set_is_refreshing(true);
    let response;
    const userEmail = auth?.getEmail();
    try {
      response = await get_company_periods_only(
        selected_companies,
        period_start,
        period_end,
        userEmail,
      );
    } catch (error) {
      console.error("Failed to fetch from OneSource, is API alive? ", error);
      toast.error("Failed to fetch from OneSource, please try again");
      set_is_refreshing(false);
      return;
    }

    const new_periods = response;
    const existing_periods = saved_data?.company_accounting_periods;
    new_periods?.forEach((new_cap) => {
      const new_start = processDateJS(new_cap?.start_date);
      const new_end = processDateJS(new_cap?.end_date);

      for (const old_cap of existing_periods) {
        if (old_cap?.company_ID !== new_cap?.company_ID) {
          continue;
        }

        const old_start = processDateJS(old_cap?.start_date);
        const old_end = processDateJS(old_cap?.end_date);

        // If the new period is the exact same as an existing period, mark the existing one green
        if (old_start?.isSame(new_start) && old_end?.isSame(new_end)) {
          old_cap.refreshing = { colour: "green" };
          return;
        }

        // If the new period has an overlap with any existing periods, mark the new period red
        if (
          new_start?.isSameOrBefore(old_end) &&
          new_end?.isSameOrAfter(old_start)
        ) {
          new_cap.refreshing = { colour: "red" };
          existing_periods?.push(new_cap);
          return;
        }
      }

      // If none of the conditions were triggered above, it means this new period doesn't have any conflicts - mark it as yellow
      new_cap.refreshing = { colour: "yellow" };
      existing_periods?.push(new_cap);
    });

    setCompanies([...companies]); // Trigger a refresh of the Table component
    set_saved_data({
      ...saved_data,
      company_accounting_periods: existing_periods,
    });
    set_selected_companies([]);
    set_is_refreshing(false);
  };

  const has_conflict = (company_id) => {
    const relevant_caps = get_relevant_periods(
      period_start,
      period_end,
      saved_data,
      company_id,
    );
    return relevant_caps?.some(
      (cap) =>
        cap?.refreshing?.colour === "red" || cap.refreshing?.color === "yellow",
    );
  };

  const rowRenderer = ({ index, key, style }) => {
    const row = companies[index];

    // Compute AP date range + count (same as your Table column)
    const companyAP = getCompanyAPsWithinPoA(row?.company_ID);
    const apRangeDisplay = (() => {
      if (companyAP && companyAP.length > 0) {
        const minDate = companyAP[0].start_date;
        const maxDate = companyAP[companyAP.length - 1].end_date;
        const dateRange = `${processDateJS(minDate)?.format("DD/MM/YYYY")} - ${processDateJS(maxDate)?.format("DD/MM/YYYY")}`;
        const numOfAPs = companyAP.length.toLocaleString();
        return `${dateRange} (#${numOfAPs})`;
      }
      return "-";
    })();

    const coverage = row?.poa_coverage ? row.poa_coverage : 0;
    const isFromOneSource = !!row?.from_onesource;

    return (
      <div
        key={key}
        role="row"
        style={{
          ...style,
          display: "grid",
          gridTemplateColumns: isClientFacing ? clientFacingGridTemplate2 : gridTemplate2,
          alignItems: "center",
          borderBottom: "1px solid #f0f0f0",
          padding: "8px 12px",
        }}
      >
        {/* Row checkbox (enabled only for OneSource companies, with tooltip otherwise) */}
        {!isClientFacing && <div>
          {isFromOneSource ? (
            <Checkbox
              hiddenInReadonly
              checked={selected_companies.includes(row)}
              onChange={(value) => {
                if (value) {
                  set_selected_companies((prev) => [...prev, row]);
                } else {
                  set_selected_companies((prev) =>
                    prev.filter((c) => c.company_ID !== row.company_ID),
                  );
                }
              }}
            />
          ) : (
            <Tooltip content={"This company was not imported from OneSource"}>
              <Checkbox disabled hiddenInReadonly />
            </Tooltip>
          )}
        </div>}

        {/* Company Name */}
        <div>{row?.company_name}</div>

        {/* Tax Office */}
        <div>{row?.tax_office || "N/A"}</div>

        {/* UTR */}
        <div>{row?.utr || "N/A"}</div>

        {/* Date Joined */}
        <div>
          {row?.date_join
            ? processDateJS(row?.date_join)?.format("DD/MM/YYYY")
            : "N/A"}
        </div>

        {/* Date Left */}
        <div>
          {row?.date_left
            ? processDateJS(row?.date_left)?.format("DD/MM/YYYY")
            : "N/A"}
        </div>

        {/* Accounting period date range (#APs) */}
        <div>{apRangeDisplay}</div>

        {/* PoA Coverage */}
        <div>
          {coverage}%
          {coverage !== 100 && <span style={{ color: "red" }}>*</span>}
        </div>

        {/* Actions: View/Edit + Conflict indicator */}
        <div className="d-flex flex-row gap-2 align-items-center">
          <Button
            viewEditButton
            neverReadonly
            style={{ width: 60 }}
            compact
            onClick={() => {
              setSelectedCompany(row.company_ID);
              set_show_ap_modal(true);
            }}
          />
          {has_conflict(row?.company_ID) && (
            <span style={{ color: "orange" }}>⚠️ Conflict</span>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Loading
        loadingType="linear"
        indeterminate={true}
        compact={false}
        className="page-loader"
      />
    );
  }

  return (
    <div className="ap-container">
      <div className="ap-container mt-4">
        <CompanyPanel />
      </div>

      {period_start && period_end ? (
        <>
          <div className="ap-container mt-4">
            <Panel style={{ paddingTop: "30px" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <p>
                  Accounting periods of UK group companies during the period of
                  account:
                </p>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <CustomTooltipIcon tooltipText={tooltips.createMissingAPs} />
                  <Button
                    loading={isCreatingAutomaticAPs}
                    hiddenInReadonly
                    onClick={createMissingAPs}
                  >
                    Populate Accounting Periods
                  </Button>
                </div>
              </div>
              <APModal
                visible={show_ap_modal}
                setVisible={set_show_ap_modal}
                selectedPeriod={selectedPeriod}
                selectedCompany={selectedCompany}
                companies={companies}
                companyAPs={getCompanyAPsWithinPoA(selectedCompany)}
                setCompanies={setCompanies}
                group_data={saved_data}
                set_group_data={set_saved_data}
                onSave={onAPModalSave}
              />

              {/* Virtualized Table with Rounded Borders */}
              <RoundedBorder>
                {/* Header Row */}
                <div
                  role="row"
                  style={{
                    display: "grid",
                    gridTemplateColumns: isClientFacing ? clientFacingGridTemplate1 : gridTemplate1, // Use the existing grid template
                    alignItems: "center",
                    padding: "8px 12px",
                    borderBottom: "1px solid #e5e7eb", // Border between header and body
                    background: "#fafafa", // Light background for the header
                    fontWeight: 600, // Bold text for the header
                  }}
                >
                  {!isClientFacing && <div>
                    <Checkbox
                      hiddenInReadonly
                      checked={all_selected}
                      onChange={(value, event) =>
                        handle_select_all(value, event)
                      }
                    />
                  </div>}

                  <div>Company Name</div>
                  <div>Tax Office</div>
                  <div>UTR</div>
                  <div>Date Joined</div>
                  <div>Date Left</div>

                  <div className="d-flex align-items-center gap-1">
                    <span>Accounting period date range</span>
                    <CustomTooltipIcon
                      tooltipText={tooltips.accountingPeriodDateRange}
                    />
                  </div>

                  <div className="d-flex align-items-center gap-1">
                    <span>PoA Coverage</span>
                    <CustomTooltipIcon tooltipText={tooltips.poaCoverage} />
                  </div>

                  <div></div>
                </div>

                {/* Virtualized List */}
                <div style={{ width: "100%", height: listHeight }}>
                  <AutoSizer disableHeight>
                    {({ width }) => (
                      <List
                        width={width}
                        height={listHeight}
                        rowCount={companies.length}
                        rowHeight={rowHeight}
                        rowRenderer={rowRenderer}
                        overscanRowCount={6}
                      />
                    )}
                  </AutoSizer>
                </div>
              </RoundedBorder>
            </Panel>
          </div>

          <div className="container-fluid mt-4">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <BackButton disabled={isPosting} />
              </div>
              <div className="d-flex gap-2">
                <Tooltip
                  content={
                    "Select companies from the table to refresh from OneSource"
                  }
                >
                  {!isClientFacing && (
                    <Button
                      hiddenInReadonly
                      onClick={refresh_from_onesource}
                      disabled={is_refreshing || isPosting}
                      kind="secondary"
                    >
                      {selected_companies?.length === 0
                        ? "Refresh from OneSource"
                        : `Refresh ${selected_companies?.length} ${selected_companies?.length === 1 ? "company" : "companies"} from OneSource`}
                    </Button>
                  )}
                </Tooltip>

                <Tooltip
                  disabled={conflictingAPs.length === 0}
                  content={
                    conflictingAPs.length > 0 &&
                    "There are unresolved conflicts after refreshing from OneSource"
                  }
                >
                  {/* Wrapping the button in a div otherwise the tooltip doesn't show when the button is disabled */}
                  <div>
                    <Button
                      hiddenInReadonly
                      loading={isPosting}
                      disabled={isError || conflictingAPs.length > 0}
                      onClick={save}
                      kind="secondary"
                    >
                      Save
                    </Button>
                  </div>
                </Tooltip>

                <Tooltip
                  disabled={conflictingAPs.length === 0}
                  content={
                    conflictingAPs.length > 0 &&
                    "There are unresolved conflicts after refreshing from OneSource"
                  }
                >
                  {/* Wrapping the button in a div otherwise the tooltip doesn't show when the button is disabled */}
                  <div>
                    <NextButton
                      loading={isPosting}
                      disabled={
                        isError ||
                        conflictingAPs.length > 0 ||
                        disableViewModeProgress
                      }
                      preNavigation={save}
                    />
                  </div>
                </Tooltip>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="ap-container mt-4">
            <Button kind="negative" onClick={() => navigate("/app/grouppoa")}>
              No Group PoA has been selected. Click here to go back
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default CompanyAP;
